/**
 * "Serve Mon" / "Serve Today" badges for the chef mealtime headings.
 *
 * Chef views bucket by DELIVERY day and show one date, full stop. This badge
 * is the single service-side fact allowed back in: when the food hits trays.
 * On Friday's list a day-before breakfast reads "Serve Mon"; a same-day lunch
 * being cooked today reads "Serve Today". Sheila's spec, 2026-07-29:
 * "Friday > Serve Mon, Thu > Serve Fri … for same day, Serve Today" — colored
 * so the days are scannable at a glance without being read.
 */

/** `YYYY-MM-DD` for the chef's local calendar day. */
export function localTodayKey(now: Date = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

const WEEKDAY_ABBREV = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const

/**
 * Weekday of a `YYYY-MM-DD` key, parsed in UTC — shipment dates are
 * UTC-midnight instants, and a local-time parse would shift every badge one
 * day west of Greenwich.
 */
function weekdayOf(dayKey: string): string {
  return WEEKDAY_ABBREV[new Date(`${dayKey}T00:00:00.000Z`).getUTCDay()] ?? dayKey
}

export interface ServeDayBadge {
  /** "Serve Today" / "Serve Mon" / … */
  label: string
  /**
   * Tone key for the color map: 'today' when the serve day IS the chef's
   * current day, else the lowercase weekday ('mon' … 'sun'). One color per
   * day so a packed Friday list separates "serve now" from "hold for Monday"
   * without reading a word.
   */
  tone: 'today' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'
}

/**
 * Badge for one serve day. `todayKey` is the chef's LOCAL day (not UTC —
 * "Today" is about the person reading the screen, unlike the date math).
 */
export function serveDayBadge(serveDayKey: string, todayKey: string = localTodayKey()): ServeDayBadge {
  if (serveDayKey === todayKey) {
    return { label: 'Serve Today', tone: 'today' }
  }
  const weekday = weekdayOf(serveDayKey)
  return { label: `Serve ${weekday}`, tone: weekday.toLowerCase() as ServeDayBadge['tone'] }
}

/**
 * Badges for a mealtime block, deduped and in serve-day order. Usually one;
 * two when a same-day and a day-before menu share the campus + mealtime on
 * one delivery day (the deliberate food-safety split).
 */
export function serveDayBadges(serveDays: readonly string[] | undefined, todayKey?: string): ServeDayBadge[] {
  if (!serveDays || serveDays.length === 0) {
    return []
  }
  return Array.from(new Set(serveDays))
    .sort()
    .map((day) => serveDayBadge(day, todayKey))
}

/**
 * One muted color per tone. Emerald = serve now; each weekday keeps a stable
 * hue so "Mon is always indigo" becomes muscle memory across screens.
 */
export const SERVE_DAY_BADGE_CLASS: Record<ServeDayBadge['tone'], string> = {
  today: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400',
  mon: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-500/15 dark:text-indigo-400',
  tue: 'bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-400',
  wed: 'bg-sky-100 text-sky-800 dark:bg-sky-500/15 dark:text-sky-400',
  thu: 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-400',
  fri: 'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400',
  sat: 'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-400',
  sun: 'bg-slate-100 text-slate-700 dark:bg-slate-500/15 dark:text-slate-400',
}
